import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormControl,
  Grid,
  InputLabel,
  Input,
  InputAdornment,
  Select,
  MenuItem,
} from '@material-ui/core';

import { Modal, ModalContents, ModalTitle, ModalButton } from 'components/Modal/Modal';
import { primaryBlue } from 'utils/color-styles';
import { updateDonationDesiredAmountAction } from 'entities/donation/actions';
import Donation, { MatchableRule } from 'entities/donation/model';
import { selectUser } from 'entities/user/selectors';

interface Props {
  donation: Donation | undefined;
  onClickClose: () => void;
}

interface DesiredMatch {
  overrideDesiredMatchAmount: number;
  matchingPercentage: number;
  matchableRule?: MatchableRule;
}

export const PartialMatchingModal = ({ donation, onClickClose }: Props) => {
  const desiredMatchingRuleObj =
    donation?.desiredMatchingRule &&
    donation?.matchableRules &&
    donation?.desiredMatchingRule in donation?.matchableRules
      ? donation?.matchableRules[donation?.desiredMatchingRule]
      : undefined;
  const [matchState, setMatchState] = useState<DesiredMatch>({
    overrideDesiredMatchAmount: Number(donation?.amountToMatch),
    matchingPercentage: donation?.matchableRule?.matchingPercentage ?? 0,
    matchableRule: desiredMatchingRuleObj ?? donation?.matchableRule,
  });
  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  const hasSelectableMatchingRuleFeature = Boolean(
    user?.organization.settings?.get('enableMatchingRuleSelection')
  );
  const hasMatchingRules =
    hasSelectableMatchingRuleFeature &&
    donation?.matchableRules &&
    Object.keys(donation?.matchableRules).length > 1;

  const amount = Number(donation?.amount || 0);
  const desiredMatchAmount = parseFloat(
    donation?.desiredMatchAmount ?? donation?.amountToMatch ?? 0
  );

  const handleMatchingRuleChange = (item) => {
    const selectedMatchData = donation?.matchableRules[item.target.value] ?? null;
    if (!selectedMatchData) {
      return;
    }
    setMatchState({
      overrideDesiredMatchAmount: Math.min(
        Number(selectedMatchData.matchableAmount),
        matchState.overrideDesiredMatchAmount
      ),
      matchingPercentage: Math.min(
        matchState.matchingPercentage,
        selectedMatchData.matchingPercentage
      ),
      matchableRule: donation?.matchableRules[item.target.value],
    });
  };

  const handlePercentageChange = (e) => {
    let newPercentage = Number(e.target.value);
    const maximumPercentage =
      (matchState.matchableRule ?? donation?.matchableRule)?.matchingPercentage ?? 0;
    newPercentage = Math.min(newPercentage, maximumPercentage);
    if (newPercentage < 0) {
      newPercentage = 0;
    }
    const newMatchedAmount = parseFloat(((newPercentage / 100) * amount).toFixed(2));
    setMatchState((prevState) => ({
      ...prevState,
      overrideDesiredMatchAmount: newMatchedAmount,
      matchingPercentage: Math.round(newPercentage),
    }));
  };

  const handleMatchedAmountChange = (e) => {
    let newMatchedAmount = Number(e.target.value);
    const maximumMatchableAmount =
      (matchState.matchableRule ?? donation?.matchableRule)?.matchableAmount ?? 0;
    newMatchedAmount = Math.min(newMatchedAmount, maximumMatchableAmount);
    if (newMatchedAmount < 0) {
      newMatchedAmount = 0;
    }
    const roundedAmount = parseFloat(newMatchedAmount.toFixed(2));
    const divisor = amount || 1;
    const newPercentage = (roundedAmount / divisor) * 100;
    setMatchState((prevState) => ({
      ...prevState,
      overrideDesiredMatchAmount: roundedAmount,
      matchingPercentage: Math.round(newPercentage),
    }));
  };

  const getMatchRatio = (matchableRuleItem: MatchableRule) =>
    Number(matchableRuleItem.matchingPercentage / 100);

  useEffect(() => {
    if (amount > 0) {
      const calculatedPercentage = (desiredMatchAmount / amount) * 100;
      setMatchState((prevState) => ({
        ...prevState,
        overrideDesiredMatchAmount: donation?.desiredMatchAmount ?? donation?.amountToMatch ?? 0,
        matchingPercentage: Math.round(calculatedPercentage),
      }));
    } else {
      setMatchState((prevState) => ({
        ...prevState,
        matchingPercentage: 0,
      }));
    }
  }, [donation?.amount, donation?.desiredMatchAmount, donation?.amountToMatch]);
  return (
    <Modal width={600} open onClose={onClickClose} style={{ minHeight: 500 }}>
      <ModalContents
        body={
          <>
            <ModalTitle style={{ marginBottom: 30 }}>Change match amount</ModalTitle>
            <div style={{ marginBottom: 10 }}>
              {hasMatchingRules && (
                <Grid style={{ marginBottom: 10 }} container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="standard-adornment-amount">Matching Rules</InputLabel>
                      <Select
                        label="Matching Rules"
                        value={matchState?.matchableRule?.id}
                        onChange={handleMatchingRuleChange}
                      >
                        {Object.values(donation?.matchableRules || {}).map((matchableRuleItem) => (
                          <MenuItem key={matchableRuleItem.id} value={matchableRuleItem.id}>
                            {matchableRuleItem.title} ({getMatchRatio(matchableRuleItem)} X )
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={3}>
                <Grid item md>
                  <FormControl>
                    <InputLabel htmlFor="standard-adornment-amount">Matching Amount</InputLabel>
                    <Input
                      id="standard-adornment-amount"
                      name="matchingAmount"
                      placeholder="Matching Amount"
                      type="number"
                      required
                      inputProps={{ inputProps: { minlength: 0 } }}
                      value={matchState.overrideDesiredMatchAmount}
                      onChange={(e) => {
                        handleMatchedAmountChange(e);
                      }}
                      startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    />
                  </FormControl>
                </Grid>
                <Grid style={{ flex: 1 }} item>
                  <FormControl>
                    <InputLabel htmlFor="standard-adornment-amount">Matching Percentage</InputLabel>
                    <Input
                      name="matchingPercentage"
                      placeholder="Matching percentage"
                      required
                      type="number"
                      style={{ flex: 1 }}
                      value={matchState.matchingPercentage}
                      onChange={(e) => {
                        handlePercentageChange(e);
                      }}
                      inputProps={{ inputProps: { min: 0, max: 100 } }}
                      endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </div>
          </>
        }
        actions={
          <>
            <ModalButton style={{ color: primaryBlue }} variant="outlined" onClick={onClickClose}>
              Cancel
            </ModalButton>
            &nbsp;
            <ModalButton
              onClick={() => {
                dispatch(
                  updateDonationDesiredAmountAction(
                    donation?.id,
                    matchState.overrideDesiredMatchAmount,
                    matchState?.matchableRule?.id
                  )
                );
                onClickClose();
              }}
            >
              Confirm
            </ModalButton>
          </>
        }
      />
    </Modal>
  );
};
